import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import blog1 from "../../data/blogs/blog1.json";
import RichTextRenderer from "../generic/RichTextRenderer";
import { Share } from "react-bootstrap-icons";
import "../../styles/Blogs.css";
import { FiUser } from "react-icons/fi";
import LoadingSpinner from "../generic/LoadingSpinner";
import { toast } from "react-toastify";

const BlogDetails = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const navigate = useNavigate();

  const getEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  const processContent = (content) => {
    if (!content?.root?.children) return content;
    const newContent = { ...content, root: { ...content.root } };

    newContent.root.children = content.root.children.map((node) => {
      if (!node.children) return node;
      return {
        ...node,
        children: node.children.flatMap((child) => {
          const url = child?.fields?.url || child?.href || child?.text;
          if (
            (child.type === "autolink" || child.type === "link") &&
            url &&
            (url.includes("youtube.com") || url.includes("youtu.be"))
          ) {
            return {
              type: "youtubeEmbed",
              fields: { url: getEmbedUrl(url) },
              children: [],
            };
          }
          return child;
        }),
      };
    });

    return newContent;
  };

 useEffect(() => {
   const blogs = [blog1];

   const blogData = blogs.find((b) => b.slug === slug);

   if (blogData) {
     blogData.content = processContent(blogData.content);
   }

   setBlog(blogData || null);
   setLoading(false);
 }, [slug]);

 useEffect(() => {
   const blogs = [blog1];

   const relatedBlogs = blogs.filter((b) => b.slug !== slug).slice(0, 3);

   setRelated(relatedBlogs);
 }, [slug]);

  if (loading) {
    return <LoadingSpinner height="70vh" />;
  }

  if (!blog) {
    return (
      <Container className="py-5 text-center text-secondary">
        <h3>Blog not found</h3>
        <Link to="/blogs">
          <Button
            variant="text-accent"
            className="rounded-pill px-4 mt-3 text-secondary"
          >
            Back to Blogs
          </Button>
        </Link>
      </Container>
    );
  }

  const {
    title,
    description,
    author,
    publishedDate,
    video,
    featuredImage,
    videoThumbnail,
    content,
  } = blog || {};

  const mediaURL = video?.url || featuredImage?.url || videoThumbnail?.url;
  const authorName =
    typeof author === "string"
      ? author
      : `${author?.firstName || ""} ${author?.lastName || ""}`.trim();

  const handleShare = async () => {
    const shareUrl = window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text: description || title,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard");
      }
    } catch (err) {
      toast.error("Unable to share");
    }
  };

  return (
    <main className="blog-details text-secondary">
      {/* HERO */}
      <section className="blog-hero-section">
        <Container
          fluid="md"
          className="py-5 px-3 px-md-5"
          style={{ maxWidth: 1100 }}
        >
          <Row>
            <Col md={8}>
              <h1
                className="blog-hero-title text-secondary"
                dangerouslySetInnerHTML={{ __html: title }}
              />

              {description && (
                <p className="blog-hero-sub lead text-secondary">
                  {description}
                </p>
              )}

              <div className="blog-hero-meta d-flex align-items-center gap-4 mt-4">
                <div className="blog-author-badge d-flex align-items-center gap-3">
                  <div
                    className="author-avatar-icon"
                    role="img"
                    aria-label="Author profile"
                  >
                    <FiUser />
                  </div>

                  <div>
                    <small className="text-secondary">By</small>
                    <div className="fw-bold">{authorName}</div>
                    <small className="text-secondary">
                      {publishedDate &&
                        new Date(publishedDate).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                    </small>
                  </div>
                </div>

                <div className="ms-auto d-flex align-items-center gap-2 blog-hero-actions">
                  {/* SHARE */}
                  <button
                    className="blog-icon-btn"
                    aria-label="Share blog"
                    onClick={handleShare}
                  >
                    <Share size={18} />
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* MEDIA */}
      {mediaURL && (
        <section className="blog-media-section">
          <Container fluid="md" style={{ maxWidth: 1100 }}>
            {video?.url?.includes("youtube") ? (
              <div className="embed-responsive">
                <iframe
                  src={getEmbedUrl(video.url)}
                  title={title}
                  allowFullScreen
                />
              </div>
            ) : video?.url ? (
              <video
                controls
                poster={videoThumbnail?.url}
                className="w-100 rounded"
              >
                <source src={video.url} type="video/mp4" />
              </video>
            ) : (
              <div
                className="featured-cover"
                style={{ backgroundImage: `url(${mediaURL})` }}
              />
            )}
          </Container>
        </section>
      )}

      {/* CONTENT */}
      <section className="blog-content-section text-secondary">
        <Container fluid="md" style={{ maxWidth: 900 }}>
          <article className="blog-article">
            {content?.root?.children?.length ? (
              <RichTextRenderer content={content.root.children} />
            ) : (
              <p className="text-muted text-center">No content available</p>
            )}
          </article>
        </Container>
      </section>

      {/* CONTINUE READING */}
      {related.length > 0 && (
        <section className="continue-reading py-5">
          <Container fluid="md" style={{ maxWidth: 1100 }}>
            <h2 className="continue-title text-secondary">
              Continue <span className="text-accent">reading</span>
            </h2>

            <Row className="g-4 mt-2">
              {related.map((r) => (
                <Col md={4} key={r._id}>
                  {/* Card structured as horizontal split: text left, image right (matches Figma) */}
                  <div className="continue-card">
                    {/* Image on top */}
                    <div
                      className="continue-thumb"
                      style={{
                        backgroundImage: `url(${
                          r.featuredImage?.url ||
                          r.videoThumbnail?.url ||
                          "/placeholder-rect.jpg"
                        })`,
                      }}
                      aria-label={r.title}
                    />

                    {/* Content below image */}
                    <div className="continue-content text-secondary">
                      <p className="continue-date">
                        {new Date(r.publishedDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </p>

                      <h3 className="continue-card-title text-secondary">
                        {r.title}
                      </h3>

                      <p className="continue-desc">{r.description}</p>

                      <div className="hm-card-bottom">
                        <button
                          type="button"
                          className="hm-readmore btn-secondary"
                          aria-label={`Read more about ${r.title}`}
                          onClick={() => {
                            navigate(`/blogs/${r.slug}`);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        >
                          Read More ↗
                        </button>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}
    </main>
  );
};

export default BlogDetails;
